import React, { useMemo, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { Link } from 'react-router';
import { format, parseISO } from 'date-fns';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import UserSVG from '@birdi/icons/user.svg';
import CalendarSVG from '@birdi/icons/calendar.svg';
import HardDriveSVG from '@birdi/icons/hard-drive.svg';
import MapSVG from '@birdi/icons/map.svg';
import GridSVG from '@birdi/icons/grid.svg';
import FolderSVG from '@birdi/icons/folder.svg';
import { BirdiTheme, forms } from '@birdi/theme';
import { stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type { MapSiteMapRow } from '@birdi/js-sdk/src/public/map-sites';
import { invalidateSites, useSiteMaps, useSitesList } from './queries';
import { ModalShell } from './site-modals';
import {
  cancelButtonCSS,
  confirmFooterCSS,
  emptyStateCSS,
  infoBannerCSS,
  listRowCSS,
  metaRowCSS,
  primaryButtonCSS,
  selectTheme,
} from './styles';
import { readAPIErrorMessage } from './utils';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const formatSize = (sizeBytes: number): string => {
  const gb = sizeBytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(sizeBytes / 1024 ** 2).toFixed(2)} MB`;
};

// Map-row metadata laid out as column groups (location+date | uploader+size |
// workspace+folder), matching the design.
const metaGroupsCSS = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 0.4rem 2rem;
`;

// Fixed-width columns so the three groups line up across rows; long values
// (e.g. a long address) wrap to the next line within their column.
const metaColCSS = (width: string) => css`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  width: ${width};
  flex-shrink: 0;
`;

// Neutral "Change site" button — same #EEEEEE (dove) fill as the Share button
// in the site header; no border, vertically centred by the row.
const changeSiteButtonCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.midnight};
  background: ${theme.dove};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${theme.mono30};
  }
`;

// Content padded away from the sand edges (rows stay flush).
const paddedBlockCSS = css`
  padding: 1rem 1.25rem;
`;

interface MapOptionData {
  value: string;
  label: string;
  subtitle: string;
  image?: string | null;
  /** Footprint overlaps this site — shown first with a "This location" badge. */
  matchesLocation?: boolean;
}

/** Add maps to a site via a multi-select map dropdown (like the dashboard). */
const AddMapModal = ({
  open,
  setOpen,
  siteId,
  existingMapIds,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  siteId: string;
  existingMapIds: string[];
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<MapOptionData[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMapOptions = async (input: string): Promise<MapOptionData[]> => {
    try {
      // Server-ranked: maps whose footprint overlaps this site come first,
      // and maps already in this site are excluded. Searches name and location.
      const res = await mapSitesAPI.listAssignableMaps(stroot('si7map'), siteId, {
        search: input || '',
        start: 1,
        limit: 20,
      });
      setError(null);
      const maps = res.body || [];
      return maps
        .filter((map) => !existingMapIds.includes(map.id))
        .map((map) => ({
          value: map.id,
          label: map.name || 'Untitled',
          subtitle: map.locationName || '',
          image: map.coverUrl,
          matchesLocation: map.matchesLocation,
        }));
    } catch (err) {
      // Swallowing this rendered every failure — a 403, a 500, a dropped
      // connection — as "No maps found", which is indistinguishable from an
      // empty workspace and hid the real fault for a long time.
      setError(readAPIErrorMessage(err));
      return [];
    }
  };

  const MapOption = (props: any) => {
    const { data, innerProps, isFocused } = props;
    return (
      <div
        {...innerProps}
        css={css`
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0.75rem;
          cursor: pointer;
          background: ${isFocused ? theme.lightOcean : 'transparent'};
        `}
      >
        <div
          css={css`
            width: 2rem;
            height: 2rem;
            border-radius: 4px;
            overflow: hidden;
            flex-shrink: 0;
            background: ${theme.mono20};
          `}
        >
          {data.image && (
            <img
              src={data.image}
              alt=""
              css={css`
                width: 100%;
                height: 100%;
                object-fit: cover;
              `}
            />
          )}
        </div>
        <div
          css={css`
            min-width: 0;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.4rem;
              font-weight: 600;
            `}
          >
            <span
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {data.label}
            </span>
            {data.matchesLocation && (
              <span
                css={(t) => css`
                  flex-shrink: 0;
                  padding: 0.1rem 0.45rem;
                  border-radius: 1rem;
                  background: ${t.lightOcean};
                  color: ${t.deepOcean};
                  font-size: 0.7rem;
                  font-weight: 600;
                `}
              >
                This location
              </span>
            )}
          </div>
          {data.subtitle && (
            <div
              css={css`
                color: ${theme.mono50};
                font-size: 0.85em;
              `}
            >
              {data.subtitle}
            </div>
          )}
        </div>
      </div>
    );
  };

  const submit = async () => {
    if (!selected.length) return;
    setSubmitting(true);
    setError(null);
    try {
      // The API assigns one map at a time.
      for (const option of selected) {
        // eslint-disable-next-line no-await-in-loop
        await mapSitesAPI.addMapToSite(stroot('ip5oht'), siteId, option.value);
      }
      invalidateSites(siteId);
      setSelected([]);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Add map to site" open={open} setOpen={setOpen}>
      <label
        htmlFor="add-map-select"
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        Map
        <AsyncSelect
          inputId="add-map-select"
          instanceId="add-map-select"
          isMulti
          isClearable={false}
          cacheOptions
          defaultOptions
          loadOptions={loadMapOptions}
          placeholder="Search for a map..."
          value={selected}
          onChange={(value: any) => setSelected(value ? [...value] : [])}
          components={{ Option: MapOption }}
          noOptionsMessage={() => 'No maps found'}
          theme={selectTheme(theme)}
          menuPortalTarget={
            typeof window !== 'undefined' ? window.document.body : undefined
          }
          styles={{
            // pointerEvents: 'auto' — the portaled menu lives in <body>, which
            // Radix sets to pointer-events: none while the modal is open;
            // without this the options render but aren't clickable.
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 9999,
              pointerEvents: 'auto',
            }),
          }}
        />
      </label>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!selected.length || submitting}
          data-testid="add-map-confirm"
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </ModalShell>
  );
};

/** Move a map to another site via a dropdown of the workspace's sites. */
/**
 * Confirm before detaching a map from its site.
 *
 * Unassigning is quiet and easy to do by accident from a row of buttons — the
 * map doesn't move anywhere visible, it just stops belonging here — so it asks
 * first. Nothing is destroyed, hence a confirm rather than a type-to-delete.
 */
const UnassignMapModal = ({
  open,
  setOpen,
  currentSiteId,
  map,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  currentSiteId: string;
  map: MapSiteMapRow;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unassign = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await mapSitesAPI.removeMapFromSite(
        stroot('unas1g'),
        currentSiteId,
        map.id,
      );
      invalidateSites(currentSiteId);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Unassign map" open={open} setOpen={setOpen}>
      <p
        css={css`
          margin-top: 0;
        `}
      >
        Remove <strong>{map.name}</strong> from this site? It will belong to no
        site, and the map itself is not deleted.
      </p>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={submitting}
          data-testid="unassign-map-confirm"
          onClick={unassign}
        >
          {submitting ? 'Unassigning…' : 'Unassign'}
        </button>
      </footer>
    </ModalShell>
  );
};

const ChangeSiteModal = ({
  open,
  setOpen,
  currentSiteId,
  map,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
  currentSiteId: string;
  map: MapSiteMapRow;
}) => {
  const theme = useTheme();
  const { data: sites } = useSitesList();
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () =>
      (sites ?? [])
        .filter((site) => site.id !== currentSiteId)
        .map((site) => ({ value: site.id, label: site.name })),
    [sites, currentSiteId],
  );

  const submit = async () => {
    if (!selectedSiteId) return;
    setSubmitting(true);
    setError(null);
    try {
      await mapSitesAPI.addMapToSite(stroot('cha3ng'), selectedSiteId, map.id);
      invalidateSites(currentSiteId);
      invalidateSites(selectedSiteId);
      setOpen(false);
    } catch (err) {
      setError(readAPIErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Change site" open={open} setOpen={setOpen}>
      <p
        css={css`
          margin-top: 0;
        `}
      >
        Move <strong>{map.name}</strong> to another site.
      </p>
      <label
        htmlFor="change-site-select"
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        Site
        <Select
          inputId="change-site-select"
          instanceId="change-site-select"
          options={options}
          value={options.find((o) => o.value === selectedSiteId) ?? null}
          onChange={(opt: any) => setSelectedSiteId(opt?.value ?? null)}
          placeholder="Select a site..."
          noOptionsMessage={() => 'No other sites'}
          theme={selectTheme(theme)}
          menuPortalTarget={
            typeof window !== 'undefined' ? window.document.body : undefined
          }
          styles={{
            menuPortal: (base: any) => ({
              ...base,
              zIndex: 9999,
              pointerEvents: 'auto',
            }),
          }}
        />
      </label>
      {error && <div css={forms.errorMessage}>{error}</div>}
      <footer css={confirmFooterCSS}>
        <button
          type="button"
          css={cancelButtonCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={primaryButtonCSS}
          disabled={!selectedSiteId || submitting}
          data-testid="change-site-confirm"
          onClick={submit}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </ModalShell>
  );
};

const MapRow = ({ map, siteId }: { map: MapSiteMapRow; siteId: string }) => {
  const [changeSiteOpen, setChangeSiteOpen] = useState(false);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const capturedDate = map.capturedAt
    ? format(parseISO(map.capturedAt), 'dd MMM yyyy')
    : null;

  return (
    <div
      css={[
        listRowCSS,
        // Anchor for the title link's stretched hit area, below.
        css`
          position: relative;
        `,
      ]}
      data-testid="site-map-row"
    >
      <div
        css={(theme) => css`
          width: 7rem;
          height: 4.5rem;
          border-radius: 8px;
          overflow: hidden;
          background: ${theme.mono20};
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${theme.mono40};
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }
        `}
      >
        {map.coverUrl ? <img src={map.coverUrl} alt={map.name} /> : <MapSVG />}
      </div>
      <div
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        `}
      >
        {/* Stretched link: the whole card opens the map, but there is still
            exactly ONE anchor — so the tab order, the focus ring and the status
            bar URL all stay on the title. Wrapping the card in an <a> instead
            would nest the "Change site" button inside a link, which is invalid
            and would navigate on click. */}
        <Link
          to={`/map/next/${map.id}`}
          css={(theme) => css`
            align-self: flex-start;
            font-weight: bold;
            color: ${theme.midnight};

            &::after {
              content: '';
              position: absolute;
              inset: 0;
              border-radius: inherit;
            }
          `}
        >
          {map.name}
        </Link>
        <div css={metaGroupsCSS}>
          <div css={metaColCSS('16rem')}>
            <div css={metaRowCSS}>
              <MapPinSVG />
              <span>{map.address || 'unknown'}</span>
            </div>
            <div css={metaRowCSS}>
              <CalendarSVG />
              <span>{capturedDate || 'unknown'}</span>
            </div>
          </div>
          <div css={metaColCSS('12rem')}>
            <div css={metaRowCSS}>
              <UserSVG />
              <span>{map.createdBy?.name || 'unknown'}</span>
            </div>
            <div css={metaRowCSS}>
              <HardDriveSVG />
              <span>
                {map.filesCount != null && map.sizeBytes != null
                  ? `${map.filesCount} files | ${formatSize(map.sizeBytes)}`
                  : 'unknown'}
              </span>
            </div>
          </div>
          <div css={metaColCSS('11rem')}>
            <div css={metaRowCSS}>
              <GridSVG />
              <span>{map.workspaceName || 'unknown'}</span>
            </div>
            <div css={metaRowCSS}>
              <FolderSVG />
              <span>{map.folderName || '-'}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Grouped so the pair reads as one action set — the row's own 1rem gap
          is too wide between two related buttons. Raised above the stretched
          card link, which would otherwise swallow both clicks. */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        `}
      >
        <button
          type="button"
          css={changeSiteButtonCSS}
          data-testid="change-site-button"
          onClick={() => setChangeSiteOpen(true)}
        >
          Change site
        </button>
        <button
          type="button"
          css={changeSiteButtonCSS}
          data-testid="unassign-map-button"
          onClick={() => setUnassignOpen(true)}
        >
          Unassign
        </button>
      </div>
      {changeSiteOpen && (
        <ChangeSiteModal
          open={changeSiteOpen}
          setOpen={setChangeSiteOpen}
          currentSiteId={siteId}
          map={map}
        />
      )}
      {unassignOpen && (
        <UnassignMapModal
          open={unassignOpen}
          setOpen={setUnassignOpen}
          currentSiteId={siteId}
          map={map}
        />
      )}
    </div>
  );
};

export const MapsTab = ({ siteId }: { siteId: string }) => {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSiteMaps(siteId);
  const [addMapOpen, setAddMapOpen] = useState(false);

  const maps = useMemo(() => (data ? data.pages.flat() : []), [data]);

  const autoGroupedThisWeek = useMemo(() => {
    const cutoff = Date.now() - SEVEN_DAYS_MS;
    return maps.filter(
      (map) =>
        map.autoGrouped &&
        // `addedAt` is the site-assignment timestamp; if it's ever absent,
        // degrade gracefully by counting all auto-grouped rows.
        (!map.addedAt || new Date(map.addedAt).getTime() >= cutoff),
    ).length;
  }, [maps]);

  if (isLoading) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
        `}
      >
        Loading ...
      </div>
    );
  }

  if (maps.length === 0) {
    return (
      <>
        <div
          css={(theme) => css`
            ${emptyStateCSS(theme)};
            ${paddedBlockCSS};
          `}
          data-testid="site-maps-empty-state"
        >
          <h3>No maps in this site yet</h3>
          <p>
            Maps are automatically grouped into sites based on their location.
            If a map isn't shown here, update its location or add it to this
            site manually.{' '}
            <button
              type="button"
              css={(theme) => css`
                color: ${theme.deepOcean};
                font-weight: bold;
                background: none;
                border: none;
                cursor: pointer;
                padding: 0;
              `}
              data-testid="add-map-button"
              onClick={() => setAddMapOpen(true)}
            >
              + Add map
            </button>
          </p>
        </div>
        <AddMapModal
          open={addMapOpen}
          setOpen={setAddMapOpen}
          siteId={siteId}
          existingMapIds={[]}
        />
      </>
    );
  }

  return (
    <div>
      {/* Top row: auto-grouped banner (left, when present) + Add map (right). */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 1rem;
          margin: 1rem 0;
        `}
      >
        {autoGroupedThisWeek > 0 && (
          <div
            css={(theme) => css`
              ${infoBannerCSS(theme)};
              margin: 0;
            `}
            data-testid="auto-grouped-banner"
          >
            <span>
              <strong>
                {autoGroupedThisWeek} new map
                {autoGroupedThisWeek === 1 ? '' : 's'}
              </strong>
              {autoGroupedThisWeek === 1 ? ' was' : ' were'} auto-grouped to
              this site this week by footprint overlap. Not the correct site?
              Use <strong>Change site</strong> on any map to reallocate it.
            </span>
          </div>
        )}
        <button
          type="button"
          css={(theme) => css`
            margin-left: auto;
            flex-shrink: 0;
            color: ${theme.deepOcean};
            font-weight: 600;
            background: none;
            border: none;
            cursor: pointer;
            padding: 0;
          `}
          data-testid="add-map-button"
          onClick={() => setAddMapOpen(true)}
        >
          + Add map
        </button>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        `}
      >
        {maps.map((map) => (
          <MapRow key={map.id} map={map} siteId={siteId} />
        ))}
      </div>
      {hasNextPage && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 1rem;
          `}
        >
          <button
            type="button"
            css={(theme) => css`
              color: ${theme.deepOcean};
              font-weight: 600;
              background: none;
              border: none;
              cursor: pointer;
            `}
            data-testid="load-more-maps"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      <AddMapModal
        open={addMapOpen}
        setOpen={setAddMapOpen}
        siteId={siteId}
        existingMapIds={maps.map((map) => map.id)}
      />
    </div>
  );
};
